import { API_BASE } from '../config/api';
import i18n from '../i18n';
import { attributionForImageUrl } from '../utils/imageSource';
import { buildGooglePhotoProxyUrl } from '../utils/googlePhotoProxy';
import CacheService from './cacheService';
import RateLimiter from './rateLimiter';
import DistanceService from './distanceService';
import TokenManager from './TokenManager';

// Legacy variables kept for potential future use
// const API_KEY = process.env.REACT_APP_GOOGLE_PLACES_API_KEY;
// const BASE_URL = 'https://maps.googleapis.com/maps/api/place';

// Backend proxy server URL
const PROXY_BASE_URL = process.env.REACT_APP_PROXY_URL || `${API_BASE}/api/places`;

// Feature flags
const USE_GOOGLE_PHOTOS = process.env.REACT_APP_ENABLE_GOOGLE_PHOTOS !== 'false'; // Enabled by default; set REACT_APP_ENABLE_GOOGLE_PHOTOS=false to disable

export class GooglePlacesService {
  // Centralized rate limit check. Returns true if the request is allowed.
  // Callers handle the rejection with their own return value ([], null, throw, etc.)
  static checkRateLimit(context = '') {
    if (!RateLimiter.canMakeRequest()) {
      console.warn(`Rate limit exceeded${context ? ` for ${context}` : ''}`);
      return false;
    }
    const limitWarnings = RateLimiter.isApproachingLimits();
    if (limitWarnings.shouldThrottle) {
      console.warn(`Approaching monthly API limit${context ? `, throttling ${context}` : ''}`);
      return false;
    }
    RateLimiter.recordRequest();
    RateLimiter.saveStats();
    return true;
  }

  // Get detailed place information
  static async getPlaceDetails(placeId) {
    try {
      // Check cache first
      const cacheKey = `details:${placeId}`;
      const cachedResult = CacheService.get(cacheKey);
      if (cachedResult) {
        console.log(`Cache hit for place details: ${placeId}`);
        return cachedResult;
      }

      if (!this.checkRateLimit('place details')) {
        throw new Error('Rate limit exceeded. Please try again later.');
      }

      const response = await fetch(`${PROXY_BASE_URL}/details/${placeId}`);
      const data = await response.json();

      if (data.status === 'success' && data.result) {
        // Cache for 60 minutes (place details don't change frequently)
        CacheService.set(cacheKey, data.result, 60);
        return data.result;
      } else {
        throw new Error(`Proxy API error: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error getting place details:', error);
      throw error;
    }
  }

  // Get photo URL from photo reference using backend proxy
  static getPhotoUrl(photoReference, maxWidth = 400, placeId = null) {
    // Mirror buildGooglePhotoProxyUrl shape (slash-encoded reference) and
    // append maxwidth which only this lazy-loader path consumes.
    const base = buildGooglePhotoProxyUrl(photoReference, placeId || '');
    return `${base}&maxwidth=${maxWidth}`;
  }

  // Photo cache for lazy loading (session-only, Google ToS compliant)
  // This cache is cleared when the page is refreshed or user navigates away
  static photoCache = new Map();
  
  // Session-only photo cache for Google ToS compliance
  // Note: Google ToS prohibits persistent caching of photo references/URLs

  // Get cached photo URL if available (for saving activities with loaded photos)
  static getCachedPhotoUrl(photoReference, placeId = null) {
    if (!photoReference) {
      return null;
    }
    
    // Check cache first with the original photoReference
    let cachedUrl = this.photoCache.get(photoReference);
    
    // If not found in cache, generate the proper URL directly
    if (!cachedUrl) {
      // Generate the correct photo URL using the photoReference
      cachedUrl = this.getPhotoUrl(photoReference, 400, placeId);
      console.log('Generated photo URL for save:', cachedUrl);
      
      // Cache it for future use
      this.photoCache.set(photoReference, cachedUrl);
    }
    
    return cachedUrl;
  }

  // Convert Google Places data to our activity format
  static convertToActivity(place) {
    // Add defensive check for invalid place data
    if (!place || !place.name) {
      console.warn('Invalid place data received in convertToActivity:', place);
      return null;
    }
    
    const priceLevel = typeof place.price_level === 'number' ? place.price_level : null;
    const priceMap = ['free', 'low', 'medium', 'high'];


    // Determine photo with priority: User photos > Wikimedia (free) > Google photos > Category fallback
    let photoUrl = null;
    let photoAttribution = null;
    let photoReference = place.photoReference || place.photos?.[0]?.photo_reference || place.photos?.[0]?.name || null;

    if (place.primaryUserPhoto?.photo_data_base64) {
      // Priority 1: User-uploaded community photo
      photoUrl = place.primaryUserPhoto.photo_data_base64;
      photoAttribution = place.primaryUserPhoto.attribution;
      console.log('📷 Using community photo for:', place.name);
    } else if (place.cover_photo) {
      // Priority 2: Backend-resolved cover (central cascade — includes Tiqets,
      // admin-curated, Wikimedia, Google in priority order).
      photoUrl = place.cover_photo;
      // Only attribute to Tiqets when the cascade actually resolved to the
      // Tiqets image. The cover may have come from a higher-priority source
      // (user/admin/Wikimedia) even when a tiqets block is attached, so keying
      // off the presence of place.tiqets alone would mislabel those photos.
      const isTiqetsCover = typeof photoUrl === 'string' && photoUrl.includes('aws-tiqets-cdn.imgix.net');
      const tiqetsCredits = place.tiqets?.image?.credits;
      if (isTiqetsCover) {
        photoAttribution = tiqetsCredits ? `Photo: ${tiqetsCredits} / Tiqets` : 'Photo: Tiqets';
      } else if (place.wikimediaPhoto?.photo_data_base64 === photoUrl
          && place.wikimediaPhoto.attribution_text) {
        // The backend resolves wikimediaPhoto through the same pick as the
        // cascade's Wikimedia branch, so URL equality means this IS the
        // rendered row — credit its own photographer, not the host.
        photoAttribution = place.wikimediaPhoto.attribution_text;
      } else {
        // Host-derived minimum (CC BY-SA needs SOME credit path on a Commons
        // cover; returns null for hosts that need none, e.g. user uploads).
        photoAttribution = attributionForImageUrl(photoUrl);
      }
    } else if (place.wikimediaPhoto?.photo_data_base64) {
      // Priority 2: Wikimedia Commons photo (free, no per-view cost)
      photoUrl = place.wikimediaPhoto.photo_data_base64;
      photoAttribution = place.wikimediaPhoto.attribution_text || null;
      console.log('📷 Using Wikimedia photo for:', place.name);
    } else if (USE_GOOGLE_PHOTOS && photoReference) {
      // Priority 3: Google Photos (only if enabled — costs $0.007/view)
      photoUrl = this.getPhotoUrl(photoReference, 400, place.place_id);
      // No photoAttribution: cards credit Google photos from the URL itself
      // (PhotoCredit), so the credit can't drift from the image shown.
      console.log('📷 Using Google photo for:', place.name);
    } else {
      // No photo: leave null so cards render their local category/gradient
      // placeholder. The old hotlinked-Unsplash category fallback is gone —
      // its tourist_attraction/default URL went dead (404) and got persisted
      // into saved_items as custom_image_url (migration 206 data-fix).
      photoUrl = null;
      photoAttribution = null;
    }
    
    // Extract coordinates from Google Places geometry
    const coordinates = place.geometry?.location ? {
      lat: typeof place.geometry.location.lat === 'function' 
        ? place.geometry.location.lat() 
        : place.geometry.location.lat,
      lng: typeof place.geometry.location.lng === 'function' 
        ? place.geometry.location.lng() 
        : place.geometry.location.lng
    } : null;


    const convertedActivity = {
      id: place.place_id,
      place_id: place.place_id,
      title: place.name,
      description: this.generateDescription(place),
      image: photoUrl,
      location: place.formatted_address || place.vicinity,
      coordinates: coordinates, // Add real coordinates from Google Places
      duration: this.estimateDuration(place.types),
      priceRange: priceLevel != null ? priceMap[priceLevel] : null,
      rating: place.rating || 4.0,
      // Score will be added by RankingService
      tags: this.generateTags(place.types),
      whyRecommended: this.generateRecommendation(place.types, place),
      timeOfDay: this.suggestTimeOfDay(place.types),
      energyLevel: this.determineEnergyLevel(place.types),
      category: this.categorizePlace(place.types),
      types: place.types || [], // Preserve Google Places types for filtering
      price_level: place.price_level || null, // Preserve Google Places price level for filtering
      photoReference: photoReference, // Store for lazy loading
      // Enhanced data fields
      user_ratings_total: place.user_ratings_total || null,
      opening_hours: place.opening_hours || null,
      website: place.website || null,
      international_phone_number: place.international_phone_number || null,
      vicinity: place.vicinity || null,
      editorial_summary: place.editorial_summary || null,
      // Google boolean attributes
      outdoor_seating: place.outdoor_seating ?? null,
      wheelchair_accessible_entrance: place.wheelchair_accessible_entrance ?? null,
      good_for_groups: place.good_for_groups ?? null,
      good_for_children: place.good_for_children ?? null,
      dine_in: place.dine_in ?? null,
      takeout: place.takeout ?? null,
      delivery: place.delivery ?? null,
      reservable: place.reservable ?? null,
      live_music: place.live_music ?? null,
      serves_vegetarian_food: place.serves_vegetarian_food ?? null,
      serves_cocktails: place.serves_cocktails ?? null,
      serves_breakfast: place.serves_breakfast ?? null,
      serves_brunch: place.serves_brunch ?? null,
      serves_coffee: place.serves_coffee ?? null,
      // Community data (enriched by backend)
      communityData: place.communityData || null,
      // Containment annotation from the search collapse pass — { place_id,
      // name } of the destination this place lives inside ("Hotel Legoland"
      // → LEGOLAND Billund Resort), or null. Drives the card's Inside badge.
      contained_in: place.contained_in || null,
      // Tiqets affiliate data — block of experience + products when this place
      // is linked via tiqets_place_links. Drives the Tickets tab + Overview chip.
      tiqets: place.tiqets || null,
      // Backend-resolved cover photo URL from the central photo cascade.
      cover_photo: place.cover_photo || null,
      googleData: place, // Keep original data for reference
      // Photo data (for ActivityCard)
      primaryUserPhoto: place.primaryUserPhoto || null,
      wikimediaPhoto: place.wikimediaPhoto || null,
      hasUserPhotos: !!place.primaryUserPhoto?.photo_data_base64,
      hasWikimediaPhoto: !!place.wikimediaPhoto?.photo_data_base64,
      photoAttribution: photoAttribution, // For both user, Wikimedia, and Google photos
      // Server-side personalized scoring (from content_scores + searchPersonalizationService)
      matchScore: place.matchScore ?? undefined,
      personalizedRank: place.personalizedRank ?? undefined,
      totalScore: place.totalScore ?? undefined
    };

    console.log('🎯 CONVERTED ACTIVITY:', convertedActivity.title, {
      user_ratings_total: convertedActivity.user_ratings_total,
      opening_hours: convertedActivity.opening_hours,
      website: convertedActivity.website,
      phone: convertedActivity.international_phone_number,
      editorial_summary: convertedActivity.editorial_summary
    });

    // Debug: Log full opening_hours structure
    if (place.opening_hours) {
      console.log('📅 OPENING HOURS DETAIL for', convertedActivity.title, ':', JSON.stringify(place.opening_hours, null, 2));
    }

    return convertedActivity;
  }

  // Helper methods
  static generateDescription(place) {
    const types = place.types || [];
    const rating = place.rating || 4.0;
    const reviewCount = place.user_ratings_total || 0;
    const reviewText = reviewCount > 1000 ? `${Math.round(reviewCount/1000)}k+ reviews` : `${reviewCount} reviews`;
    
    // More specific type combinations
    if (types.includes('shopping_mall') && types.includes('tourist_attraction')) {
      return `Popular shopping destination and tourist attraction with ${rating}/5 stars (${reviewText}). Features retail stores, dining, and entertainment.`;
    } else if (types.includes('shopping_mall')) {
      return `Shopping center with ${rating}/5 stars (${reviewText}). Browse stores, dine, and enjoy retail experiences.`;
    } else if (types.includes('restaurant') || types.includes('food')) {
      return `Highly rated dining experience with ${rating}/5 stars (${reviewText}). Great for food lovers seeking local flavors.`;
    } else if (types.includes('museum') && types.includes('tourist_attraction')) {
      return `Popular museum and cultural attraction with ${rating}/5 stars (${reviewText}). Features exhibits, artifacts, and educational experiences.`;
    } else if (types.includes('park') && types.includes('tourist_attraction')) {
      return `Scenic park and popular attraction with ${rating}/5 stars (${reviewText}). Perfect for outdoor activities and sightseeing.`;
    } else if (types.includes('tourist_attraction')) {
      const typeHint = this.getTypeHint(types);
      return `Popular ${typeHint} attraction with ${rating}/5 stars (${reviewText}). A must-see destination for visitors and locals.`;
    } else if (types.includes('museum')) {
      return `Cultural museum with ${rating}/5 stars (${reviewText}). Offers fascinating exhibits and learning opportunities.`;
    } else if (types.includes('park')) {
      return `Beautiful park with ${rating}/5 stars (${reviewText}). Perfect for outdoor activities and enjoying nature.`;
    } else if (types.includes('art_gallery')) {
      return `Art gallery with ${rating}/5 stars (${reviewText}). Features creative works and cultural exhibitions.`;
    } else {
      return `Local favorite with ${rating}/5 stars (${reviewText}). Highly recommended by people who know the area.`;
    }
  }

  static getTypeHint(types) {
    if (types.includes('shopping_mall')) return 'shopping';
    if (types.includes('amusement_park')) return 'entertainment';
    if (types.includes('zoo')) return 'family';
    if (types.includes('aquarium')) return 'educational';
    if (types.includes('art_gallery')) return 'cultural';
    if (types.includes('spa')) return 'wellness';
    if (types.includes('gym')) return 'fitness';
    return 'local';
  }

  static generateTags(types = []) {
    const tagMap = {
      'restaurant': ['Food & Drink', 'Local Cuisine'],
      'tourist_attraction': ['Must-See', 'Popular'],
      'museum': ['Educational', 'Cultural', 'Indoor'],
      'park': ['Outdoor', 'Nature', 'Family-friendly'],
      'amusement_park': ['Entertainment', 'Family', 'Active'],
      'shopping_mall': ['Shopping', 'Indoor'],
      'art_gallery': ['Art', 'Cultural', 'Creative'],
      'zoo': ['Family', 'Educational', 'Animals'],
      'aquarium': ['Family', 'Educational', 'Indoor']
    };

    const tags = new Set(['Recommended']);
    types.forEach(type => {
      if (tagMap[type]) {
        tagMap[type].forEach(tag => tags.add(tag));
      }
    });

    return Array.from(tags).slice(0, 4);
  }

  static generateRecommendation(types = [], place) {
    const reviewCount = place?.user_ratings_total || 0;
    
    if (types.includes('shopping_mall') && types.includes('tourist_attraction')) {
      return "Perfect for shopping enthusiasts and sightseers - combines retail therapy with local attractions";
    } else if (types.includes('shopping_mall')) {
      return "Great destination for shopping, dining, and entertainment all in one location";
    } else if (types.includes('restaurant') || types.includes('food')) {
      return reviewCount > 5000 ? "Highly popular dining spot beloved by thousands of food lovers" : "Based on your interest in food and local dining experiences";
    } else if (types.includes('museum') && types.includes('tourist_attraction')) {
      return "Perfect blend of education and sightseeing - ideal for cultural exploration";
    } else if (types.includes('park') && types.includes('tourist_attraction')) {
      return "Combines natural beauty with popular attractions - great for outdoor lovers and sightseers";
    } else if (types.includes('museum')) {
      return "Perfect match for your educational and cultural interests";
    } else if (types.includes('park')) {
      return "Great for your outdoor and nature preferences";
    } else if (types.includes('tourist_attraction')) {
      return reviewCount > 10000 ? "Must-see destination with thousands of positive visitor experiences" : "Popular attraction recommended by visitors and locals alike";
    } else {
      return reviewCount > 1000 ? "Highly rated local favorite with proven visitor satisfaction" : "Recommended by locals who know the best spots in the area";
    }
  }

  static estimateDuration(types = []) {
    if (types.includes('restaurant')) return '1-2 hours';
    if (types.includes('museum')) return '2-3 hours';
    if (types.includes('park')) return '2-4 hours';
    if (types.includes('tourist_attraction')) return '1-3 hours';
    return '1-2 hours';
  }

  static suggestTimeOfDay(types = []) {
    if (types.includes('restaurant')) return ['evening'];
    if (types.includes('museum')) return ['morning', 'afternoon'];
    if (types.includes('park')) return ['morning', 'afternoon'];
    return ['morning', 'afternoon', 'evening'];
  }

  static determineEnergyLevel(types = []) {
    if (types.includes('amusement_park')) return 'high';
    if (types.includes('park')) return 'moderate';
    if (types.includes('museum')) return 'low';
    return 'moderate';
  }

  // Comprehensive category mapping with sub-category support
  static getCategoryApiTypes(category, subCategory = null) {
    const categoryMapping = {
      'restaurants': {
        'all': ['restaurant', 'meal_takeaway', 'meal_delivery', 'cafe', 'bakery', 'ice_cream_shop', 'dessert_shop', 'dessert_restaurant'],
        'italian': ['italian_restaurant', 'pizza_restaurant'],
        'asian': ['asian_restaurant', 'chinese_restaurant', 'japanese_restaurant', 'korean_restaurant', 'thai_restaurant', 'vietnamese_restaurant', 'indian_restaurant'],
        'chinese': ['chinese_restaurant'],
        'japanese': ['japanese_restaurant', 'sushi_restaurant'],
        'korean': ['korean_restaurant'],
        'vietnamese': ['vietnamese_restaurant'],
        'indian': ['indian_restaurant'],
        'thai': ['thai_restaurant'],
        'american': ['american_restaurant', 'steak_house', 'hamburger_restaurant'],
        'mexican': ['mexican_restaurant'],
        'french': ['french_restaurant'],
        'seafood': ['seafood_restaurant', 'sushi_restaurant'],
        'cafes': ['cafe', 'coffee_shop', 'bakery', 'donut_shop'],
        'desserts': ['ice_cream_shop', 'dessert_shop', 'dessert_restaurant', 'chocolate_shop', 'candy_store', 'bakery'],
        'fast_food': ['fast_food_restaurant', 'meal_takeaway', 'meal_delivery', 'sandwich_shop', 'deli']
      },
      'bars': {
        'all': ['bar', 'night_club', 'casino', 'pub', 'wine_bar', 'karaoke', 'comedy_club'],
        'cocktails': ['bar', 'wine_bar'],
        'breweries': ['pub', 'bar'],
        'wine': ['wine_bar', 'bar'],
        'nightlife': ['night_club', 'casino'],
        'entertainment': ['karaoke', 'comedy_club']
      },
      'parks': {
        // Google Places API v1 Compatible Parks & Outdoor Types (merged outdoor + parks)
        'all': ['hiking_area', 'observation_deck', 'park', 'national_park', 'state_park', 'adventure_sports_center', 'cycling_park', 'botanical_garden', 'garden', 'wildlife_park', 'dog_park', 'barbecue_area', 'picnic_ground', 'skateboard_park', 'beach', 'campground', 'marina'],
        'nature': ['park', 'national_park', 'state_park', 'botanical_garden', 'garden', 'wildlife_park', 'wildlife_refuge', 'dog_park'],
        'playgrounds': ['playground', 'park'],
        'hiking': ['hiking_area', 'park', 'national_park', 'state_park', 'observation_deck'],
        'sports': ['sports_complex', 'sports_club', 'sports_activity_location', 'sports_coaching', 'stadium', 'arena', 'athletic_field', 'playground', 'fitness_center', 'gym'],
        'water': ['beach', 'swimming_pool', 'water_park', 'marina', 'lake'],
        'winter': ['ski_resort', 'ice_skating_rink'],
        'adventure': ['adventure_sports_center', 'cycling_park', 'skateboard_park'],
        'camping': ['campground', 'picnic_ground', 'barbecue_area'],
        'golf': ['golf_course']
      },
      'museums': {
        // Google Places API v1 Compatible Museums/Attractions Types
        'all': ['museum', 'art_gallery', 'cultural_landmark', 'historical_place', 'monument', 'performing_arts_theater', 'art_studio', 'auditorium', 'sculpture'],
        'art': ['art_gallery', 'museum', 'cultural_landmark', 'art_studio', 'sculpture'],
        'history': ['museum', 'historical_place', 'monument'],
        'performances': ['performing_arts_theater', 'auditorium']
      },
      'entertainment': {
        // Google Places API v1 Compatible Entertainment Types
        'all': [
          // Gaming & Interactive Entertainment
          'bowling_alley', 'amusement_center', 'video_arcade',
          
          // Shows & Performances  
          'movie_theater', 'comedy_club', 'performing_arts_theater', 'auditorium', 'amphitheatre', 'concert_hall',
          
          // Attractions & Experiences
          'amusement_park', 'water_park', 'aquarium', 'zoo', 'planetarium',
          
          // Nightlife & Social
          'casino', 'night_club', 'bar', 'pub', 'karaoke', 'wine_bar',
          
          // Cultural Entertainment
          'art_gallery', 'museum', 'cultural_center',
          
          // Sports & Recreation
          'sports_complex', 'golf_course',
          
          // Events & Venues
          'event_venue'
        ],
        'movies': ['movie_theater'],
        'gaming': ['bowling_alley', 'casino', 'video_arcade', 'amusement_center'],
        'sports': ['sports_complex', 'golf_course'],
        'music': ['night_club', 'bar', 'pub', 'concert_hall', 'performing_arts_theater', 'amphitheatre', 'auditorium'],
        'venues': ['performing_arts_theater', 'concert_hall', 'movie_theater', 'auditorium', 'amphitheatre', 'event_venue'],
        'family': ['zoo', 'aquarium', 'video_arcade', 'amusement_center', 'amusement_park', 'water_park']
      },
      'shopping': {
        'all': ['shopping_mall', 'department_store', 'clothing_store', 'electronics_store', 'jewelry_store', 'shoe_store', 'book_store', 'store', 'toy_store', 'gift_shop', 'sporting_goods_store', 'pet_store', 'souvenir_shop'],
        'clothing': ['clothing_store', 'shoe_store'],
        'electronics': ['electronics_store'],
        'home': ['furniture_store', 'home_goods_store'],
        'books': ['book_store'],
        'gifts': ['gift_shop', 'souvenir_shop', 'toy_store'],
        'jewelry': ['jewelry_store'],
        'malls': ['shopping_mall', 'department_store'],
        'specialty': ['gift_shop', 'souvenir_shop', 'book_store', 'toy_store']
      },
      'grocery': {
        'all': ['grocery_store', 'supermarket', 'convenience_store', 'pharmacy'],
        'supermarkets': ['grocery_store', 'supermarket'],
        'convenience': ['convenience_store'],
        'pharmacy': ['pharmacy'],
        'essentials': ['grocery_store', 'supermarket', 'convenience_store', 'pharmacy']
      },
      'attractions': {
        'all': ['tourist_attraction', 'historical_place', 'monument', 'point_of_interest', 'landmark', 'cultural_landmark', 'observation_deck', 'visitor_center', 'scenic_point']
      },
      'tourist_attractions': {
        'all': ['tourist_attraction', 'historical_place', 'monument', 'point_of_interest', 'landmark', 'cultural_landmark', 'observation_deck', 'visitor_center', 'scenic_point']
      },
      'educational': {
        'all': ['museum', 'aquarium', 'zoo', 'planetarium', 'visitor_center', 'science_museum'],
        'museums': ['museum', 'planetarium', 'science_museum', 'aquarium', 'zoo'],
        'tours': ['visitor_center']
      },
      'wellness': {
        'all': ['spa', 'beauty_salon', 'hair_care', 'gym', 'yoga_studio', 'massage_therapist', 'swimming_pool', 'fitness_center', 'hair_salon', 'barber_shop', 'nail_salon', 'makeup_artist', 'tanning_studio'],
        'fitness': ['gym', 'yoga_studio', 'fitness_center', 'swimming_pool'],
        'spa': ['spa', 'beauty_salon', 'hair_care'],
        'beauty': ['beauty_salon', 'hair_salon', 'barber_shop', 'nail_salon', 'makeup_artist', 'tanning_studio']
      },
      'transportation': {
        'all': ['gas_station', 'car_rental', 'car_wash', 'parking', 'electric_vehicle_charging_station', 'airport', 'train_station', 'bus_station', 'subway_station'],
        'automotive': ['gas_station', 'car_rental', 'car_wash'],
        'fuel': ['gas_station', 'electric_vehicle_charging_station', 'rest_stop'],
        'parking': ['parking'],
        'transit': ['airport', 'train_station', 'bus_station', 'subway_station', 'ferry_terminal']
      },
      'lodging': {
        'all': ['hotel', 'motel', 'bed_and_breakfast', 'hostel', 'resort_hotel', 'guest_house', 'inn', 'campground', 'rv_park', 'camping_cabin'],
        'hotels': ['hotel', 'motel', 'resort_hotel'],
        'budget': ['hostel', 'motel', 'guest_house'],
        'unique': ['bed_and_breakfast', 'inn'],
        'outdoor': ['campground', 'rv_park', 'camping_cabin']
      }
    };
    
    // Handle sub-category requests
    if (subCategory && categoryMapping[category] && typeof categoryMapping[category] === 'object') {
      return categoryMapping[category][subCategory] || categoryMapping[category]['all'] || ['tourist_attraction'];
    }
    
    // Handle main category requests
    if (categoryMapping[category]) {
      if (Array.isArray(categoryMapping[category])) {
        return categoryMapping[category];
      } else {
        return categoryMapping[category]['all'] || ['tourist_attraction'];
      }
    }
    
    return ['tourist_attraction'];
  }

  static categorizePlace(types = []) {
    // Updated categorization for new 12-category system
    if (types.some(type => ['restaurant', 'meal_takeaway', 'cafe', 'bakery', 'food', 'ice_cream_shop', 'dessert_shop', 'dessert_restaurant', 'chocolate_shop', 'candy_store', 'donut_shop', 'sandwich_shop', 'deli'].includes(type))) return 'restaurants';
    if (types.some(type => ['bar', 'night_club', 'casino', 'pub', 'wine_bar', 'karaoke', 'comedy_club'].includes(type))) return 'bars';
    if (types.some(type => ['park', 'national_park', 'state_park', 'hiking_area', 'observation_deck', 'adventure_sports_center', 'cycling_park', 'botanical_garden', 'garden', 'wildlife_park', 'dog_park', 'barbecue_area', 'picnic_ground', 'skateboard_park', 'zoo', 'aquarium', 'amusement_park'].includes(type))) return 'parks';
    if (types.some(type => ['museum', 'art_gallery', 'cultural_landmark', 'historical_place', 'monument', 'performing_arts_theater', 'art_studio', 'auditorium', 'sculpture'].includes(type))) return 'museums';
    // Use the same entertainment types as API search (lines 559-587) for consistency
    const entertainmentTypes = [
      // Gaming & Interactive Entertainment
      'bowling_alley', 'amusement_center', 'video_arcade',
      
      // Shows & Performances  
      'movie_theater', 'comedy_club', 'performing_arts_theater', 'auditorium', 'amphitheatre', 'concert_hall',
      
      // Attractions & Experiences
      'amusement_park', 'water_park', 'aquarium', 'zoo', 'planetarium',
      
      // Nightlife & Social
      'casino', 'night_club', 'bar', 'pub', 'karaoke', 'wine_bar',
      
      // Cultural Entertainment
      'art_gallery', 'museum', 'cultural_center',
      
      // Sports & Recreation
      'sports_complex', 'golf_course',
      
      // Events & Venues
      'event_venue'
    ];
    if (types.some(type => entertainmentTypes.includes(type))) return 'entertainment';
    if (types.some(type => ['shopping_mall', 'department_store', 'clothing_store', 'electronics_store', 'jewelry_store', 'shoe_store', 'book_store', 'store', 'toy_store', 'gift_shop', 'sporting_goods_store', 'pet_store', 'souvenir_shop'].includes(type))) return 'shopping';
    if (types.some(type => ['grocery_store', 'supermarket', 'convenience_store', 'pharmacy'].includes(type))) return 'grocery';
    if (types.some(type => ['planetarium', 'visitor_center', 'science_museum', 'aquarium', 'zoo'].includes(type))) return 'educational';
    if (types.some(type => ['spa', 'beauty_salon', 'hair_care', 'gym', 'yoga_studio', 'massage_therapist', 'swimming_pool', 'fitness_center', 'hair_salon', 'barber_shop', 'nail_salon', 'makeup_artist', 'tanning_studio'].includes(type))) return 'wellness';
    if (types.some(type => ['gas_station', 'car_rental', 'car_wash', 'parking', 'electric_vehicle_charging_station', 'airport', 'train_station', 'bus_station', 'subway_station', 'ferry_terminal', 'rest_stop'].includes(type))) return 'transportation';
    if (types.some(type => ['hotel', 'motel', 'bed_and_breakfast', 'hostel', 'resort_hotel', 'guest_house', 'inn', 'campground', 'rv_park', 'camping_cabin'].includes(type))) return 'lodging';
    if (types.includes('tourist_attraction')) return 'attraction';
    return 'other';
  }

  // Helper method to check if location is popular (for longer caching)
  static isPopularLocation(location) {
    const popularCities = [
      'san francisco', 'new york', 'los angeles', 'chicago', 'miami',
      'las vegas', 'seattle', 'boston', 'washington dc', 'philadelphia',
      'london', 'paris', 'tokyo', 'sydney', 'toronto', 'vancouver'
    ];
    return popularCities.some(city => 
      location.toLowerCase().includes(city.toLowerCase())
    );
  }



  // Add distance calculations to search results
  static async addDistancesToResults(results, startingPointCoordinates) {
    const destinations = results.map(place => {
      const coords = place.geometry?.location;
      if (!coords) return null;
      
      return {
        lat: typeof coords.lat === 'function' ? coords.lat() : coords.lat,
        lng: typeof coords.lng === 'function' ? coords.lng() : coords.lng
      };
    }).filter(Boolean);

    if (destinations.length === 0) return results;

    try {
      // Use batch distance calculation for efficiency
      const distanceResults = await DistanceService.calculateDistances(
        startingPointCoordinates, 
        destinations
      );

      // Merge distance data back into results
      return results.map((place, index) => {
        const coords = place.geometry?.location;
        if (!coords) return place;

        const distanceResult = distanceResults[index];
        if (distanceResult && distanceResult.status === 'OK') {
          return {
            ...place,
            distance: {
              distance: distanceResult.distance,
              duration: distanceResult.duration,
              status: distanceResult.status,
              method: distanceResult.method
            }
          };
        }

        return place;
      });
    } catch (error) {
      console.error('Error calculating distances for results:', error);
      return results; // Return original results without distances
    }
  }

  // Get autocomplete suggestions for search queries
  static async getAutocompleteSuggestions(query, location) {
    try {
      if (query.length < 2) return [];
      
      // Check cache first
      const cacheKey = `autocomplete:${query.toLowerCase()}:${location.toLowerCase()}`;
      const cachedResult = CacheService.get(cacheKey);
      if (cachedResult) {
        console.log(`Autocomplete cache hit: ${query}`);
        return cachedResult;
      }

      if (!this.checkRateLimit('autocomplete')) {
        return this.getFallbackAutocompleteSuggestions(query, location);
      }

      // Use dedicated autocomplete endpoint
      const params = new URLSearchParams({
        input: query,
        location: location
      });

      console.log(`Making autocomplete request: ${query} in ${location}`);
      const response = await fetch(`${PROXY_BASE_URL}/autocomplete?${params}`);
      
      if (!response.ok) {
        throw new Error(`Autocomplete API error: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.status === 'success' && data.predictions) {
        // Cache for 10 minutes
        CacheService.set(cacheKey, data.predictions, 10);
        return data.predictions;
      } else {
        throw new Error(`Autocomplete error: ${data.error || 'Unknown error'}`);
      }
      
    } catch (error) {
      console.error('Error getting autocomplete suggestions:', error);
      return this.getFallbackAutocompleteSuggestions(query, location);
    }
  }

  // Fallback autocomplete suggestions when API is unavailable
  static getFallbackAutocompleteSuggestions(query, location) {
    const queryLower = query.toLowerCase();
    const fallbackSuggestions = [
      { description: `${query} restaurants in ${location}`, main_text: `${query} restaurants`, secondary_text: location },
      { description: `${query} attractions in ${location}`, main_text: `${query} attractions`, secondary_text: location },
      { description: `${query} museums in ${location}`, main_text: `${query} museums`, secondary_text: location },
      { description: `${query} parks in ${location}`, main_text: `${query} parks`, secondary_text: location },
      { description: `${query} activities in ${location}`, main_text: `${query} activities`, secondary_text: location }
    ];

    return fallbackSuggestions
      .filter(suggestion => suggestion.main_text.toLowerCase().includes(queryLower))
      .slice(0, 3)
      .map(suggestion => ({
        description: suggestion.description,
        place_id: `fallback_${Date.now()}_${Math.random()}`,
        structured_formatting: {
          main_text: suggestion.main_text,
          secondary_text: suggestion.secondary_text
        },
        types: ['establishment']
      }));
  }

  // Place autocomplete - for landmarks, addresses, any place (not just cities)
  // Returns suggestions with place_id that can be used to get coordinates
  static async getPlaceAutocompleteSuggestions(input, locationBias = null) {
    try {
      if (input.length < 2) return [];

      // Check cache first
      const biasKey = locationBias ? `${locationBias.lat.toFixed(2)},${locationBias.lng.toFixed(2)}` : 'none';
      const cacheKey = `place-autocomplete:${input.toLowerCase()}:${biasKey}`;
      const cachedResult = CacheService.get(cacheKey);
      if (cachedResult) {
        console.log(`Place autocomplete cache hit: ${input}`);
        return cachedResult;
      }

      if (!this.checkRateLimit('place autocomplete')) return [];

      const params = new URLSearchParams({ input });

      // Add location bias if provided (lat,lng)
      if (locationBias && locationBias.lat && locationBias.lng) {
        params.set('locationBias', `${locationBias.lat},${locationBias.lng}`);
      }

      console.log(`📍 Place autocomplete request: "${input}"`);
      const response = await fetch(`${PROXY_BASE_URL}/place-autocomplete?${params}`);

      if (!response.ok) {
        throw new Error(`Place autocomplete API error: ${response.status}`);
      }

      const data = await response.json();

      if (data.status === 'success' && data.predictions) {
        // Cache for 10 minutes
        CacheService.set(cacheKey, data.predictions, 10);
        return data.predictions;
      } else {
        console.error('Place autocomplete error:', data.error || 'Unknown error');
        return [];
      }
    } catch (error) {
      console.error('Error getting place autocomplete suggestions:', error);
      return [];
    }
  }

  // City autocomplete - specifically for cities/localities
  // Returns suggestions filtered to only cities (locality, administrative_area_level_3, sublocality)
  /**
   * Resolve a city name to coordinates via the local GeoNames endpoint —
   * free, no Google call. For picks that arrive without coordinates (the
   * recents/popular list shown on focus, before any query runs).
   *
   * Returns coordinates ONLY on an exact description match. A near match
   * ("Springfield, Illinois, US" → "Springfield, United States") would pin
   * the stop to the wrong city, so ambiguity returns null and the caller
   * leaves coordinates unset — the server-side save backstop then resolves
   * it, or it stays null rather than becoming wrong.
   *
   * `bareName`: a name with no comma ("Lisbon", "Lisboa" — a day's city or
   * text typed into Trip setup) names no region that could disagree, so it
   * also takes the first city that name exactly is (/cities lists exact
   * names first, largest first) — what Google's top prediction gave those
   * callers. A prefix ("Lisb") is not a name and still returns null.
   */
  static async lookupCityCoords(name, { bareName = false } = {}) {
    const query = String(name || '').trim();
    if (query.length < 2) return null;
    try {
      const response = await fetch(`${PROXY_BASE_URL}/cities?q=${encodeURIComponent(query)}`);
      if (!response.ok) return null;
      const data = await response.json();
      const predictions = data.predictions || [];
      let match = predictions.find(p =>
        String(p.description || '').trim().toLowerCase() === query.toLowerCase());
      if (!match && bareName && !query.includes(',')) {
        match = predictions.find(p => p.exact_match === true);
      }
      if (!match || !Number.isFinite(match.latitude) || !Number.isFinite(match.longitude)) return null;
      return { lat: match.latitude, lng: match.longitude };
    } catch (error) {
      console.warn('City coordinate lookup failed:', error);
      return null;
    }
  }

  static async getCityAutocompleteSuggestions(input) {
    try {
      if (input.length < 2) return [];

      // Check cache first
      const cacheKey = `city-autocomplete:${input.toLowerCase()}`;
      const cachedResult = CacheService.get(cacheKey);
      if (cachedResult) {
        console.log(`City autocomplete cache hit: ${input}`);
        return cachedResult;
      }

      // Local-first: GeoNames-backed /cities is instant, free, and returns
      // coordinates in each prediction (no place-details round-trip on
      // pick). Google is the fallback for what cities500 doesn't cover —
      // neighborhoods, regions, typo correction.
      try {
        const localResponse = await fetch(`${PROXY_BASE_URL}/cities?q=${encodeURIComponent(input)}`);
        if (localResponse.ok) {
          const localData = await localResponse.json();
          if (localData.status === 'success' && localData.predictions?.length > 0) {
            CacheService.set(cacheKey, localData.predictions, 10);
            return localData.predictions;
          }
        }
      } catch (localError) {
        console.warn('Local city lookup failed, falling back to Google:', localError);
      }

      // Rate limit guards the metered Google path only — local lookups above
      // are unmetered and must not burn the budget.
      if (!this.checkRateLimit('city autocomplete')) return [];

      const params = new URLSearchParams({ input });

      console.log(`🏙️ City autocomplete request: "${input}"`);
      const response = await fetch(`${PROXY_BASE_URL}/city-autocomplete?${params}`);

      if (!response.ok) {
        throw new Error(`City autocomplete API error: ${response.status}`);
      }

      const data = await response.json();

      if (data.status === 'success' && data.predictions) {
        // Cache for 10 minutes
        CacheService.set(cacheKey, data.predictions, 10);
        return data.predictions;
      } else {
        console.error('City autocomplete error:', data.error || 'Unknown error');
        return [];
      }
    } catch (error) {
      console.error('Error getting city autocomplete suggestions:', error);
      return [];
    }
  }

  // Optimized batch search - single API call with multiple types.
  // Returns the result array directly. Throws DAILY_COST_LIMIT_EXCEEDED and
  // GOOGLE_API_FAILED so callers can show the cost-cap banner or a "search
  // unavailable" error; all other errors return [].
  static async batchSearch(location, types = ['restaurants', 'tourist attractions', 'museums'], options = {}) {
    try {
      return await this.searchPlacesOptimized(location, types, options);
    } catch (error) {
      console.error(`Error in optimized batch search for ${location}:`, error);
      if (error.code === 'DAILY_COST_LIMIT_EXCEEDED' || error.code === 'GOOGLE_API_FAILED') throw error;
      return [];
    }
  }

  // New optimized search method using includedTypes
  static async searchPlacesOptimized(location, types, options = {}) {

    if (!this.checkRateLimit('optimized search')) return [];

    try {
      // "Load more" re-runs the same search with excludeIds (no query or
      // location variation — that wandered to "<city> suburbs", BH-08-05).
      const cacheKey = `optimized:${location}:${types.join(',')}`;

      const params = new URLSearchParams({
        location,
        query: options.query || '',
        includedTypes: JSON.stringify(types)
      });
      
      // Add excluded IDs to avoid duplicates
      if (options.excludeIds && options.excludeIds.length > 0) {
        params.append('excludeIds', JSON.stringify(options.excludeIds));
        console.log(`🔄 LOAD MORE: Excluding ${options.excludeIds.length} existing activities`);
      }

      // Pass coordinates for location-based search (nearby / search this area)
      if (options.lat != null && options.lng != null) {
        params.append('lat', options.lat);
        params.append('lng', options.lng);
      }
      if (options.radius != null) {
        params.append('radius', options.radius);
      }
      // Pass exact viewport rectangle so server can hard-clip results to the visible map area
      if (options.bounds && typeof options.bounds === 'object') {
        params.append('bounds', JSON.stringify(options.bounds));
      }
      // Force API refresh (bypass smart cache) for explicit user actions
      if (options.forceRefresh) {
        params.append('forceRefresh', 'true');
      }
      // Search mode: 'focused' returns only Google's just-matched venues (used by
      // the trip-edit Map tab text search). Default 'discovery' is the cache-soup
      // expansion used by the home/results page.
      if (options.mode) {
        params.append('mode', options.mode);
      }
      // Pass openNow and price filters to backend
      if (options.openNow) {
        params.append('openNow', 'true');
      }
      if (options.priceLevels && options.priceLevels.length < 5) {
        params.append('priceLevels', JSON.stringify(options.priceLevels));
      }
      // Pass companion IDs for group-aware ranking (Who's Going)
      if (options.companionUserIds?.length > 0) {
        params.append('companionUserIds', options.companionUserIds.join(','));
      }
      if (options.companionChildIds?.length > 0) {
        params.append('companionChildIds', options.companionChildIds.join(','));
      }
      // Pass group weight distribution for weighted ranking
      if (options.groupWeights && Object.keys(options.groupWeights).length > 0) {
        params.append('groupWeights', JSON.stringify(options.groupWeights));
      }

      const response = await fetch(`${PROXY_BASE_URL}/search?${params}`, {
        headers: TokenManager.getAuthHeaders()
      });

      if (!response.ok) {
        // Read the body: the daily cost cap answers 429 with a coded body the
        // Search page shows as its own banner (BH-08-02). A plain Error here
        // would fall through to the catch below and read as "no results".
        let body = null;
        try { body = await response.json(); } catch { /* non-JSON error page */ }
        if (body?.error === 'DAILY_COST_LIMIT_EXCEEDED') {
          const err = new Error(body.beta_message || body.message || 'Daily search limit reached');
          err.code = 'DAILY_COST_LIMIT_EXCEEDED';
          throw err;
        }
        if (response.status >= 500 || response.status === 429) {
          const err = new Error(body?.message || 'Search service unavailable');
          err.code = 'GOOGLE_API_FAILED';
          throw err;
        }
        throw new Error(`Proxy server error: ${response.status}`);
      }

      const data = await response.json();

      if (data.status === 'success' && data.results) {
        let results = data.results;

        // Convert to standard format and add enhanced data
        results = results.map(place => this.convertToActivity(place));

        // Cache successful results (a load-more page is not the search's first page)
        if (!options.excludeIds?.length) {
          CacheService.set(cacheKey, results, this.isPopularLocation(location) ? 7200 : 3600);
        }

        console.log(`Optimized search returned ${results.length} results for ${location}`);
        return results;
      } else {
        console.error('Optimized API error:', data.error || 'Unknown error');
        // Surface upstream Google API failures so callers (e.g. focused-mode map
        // search) can show "search failed" instead of "no results".
        if (data.error === 'GOOGLE_API_FAILED') {
          const err = new Error(data.message || 'Google search service unavailable');
          err.code = 'GOOGLE_API_FAILED';
          throw err;
        }
        return [];
      }
    } catch (error) {
      if (error.code === 'GOOGLE_API_FAILED' || error.code === 'DAILY_COST_LIMIT_EXCEEDED') throw error;
      console.error('Optimized API call failed:', error);
      return [];
    }
  }

  // Determine search parameters based on map bounds and zoom
  static getSearchParametersFromBounds(bounds, zoom) {
    const center = {
      lat: (bounds.north + bounds.south) / 2,
      lng: (bounds.east + bounds.west) / 2
    };
    
    // Calculate approximate radius from bounds
    const radius = this.calculateRadiusFromBounds(bounds);
    
    if (zoom <= 11) {
      // City-wide view: use rectangular bounds
      return {
        type: 'bounds',
        bounds: bounds,
        searchStrategy: 'wide_area',
        center: center,
        radius: radius
      };
    } else if (zoom <= 13) {
      // District view: use large radius
      return {
        type: 'radius',
        center: center,
        radius: Math.min(radius, 25000), // Cap at 25km
        searchStrategy: 'district'
      };
    } else {
      // Neighborhood view: use moderate radius
      return {
        type: 'radius',
        center: center,
        radius: Math.min(radius, 8000), // Cap at 8km
        searchStrategy: 'neighborhood'
      };
    }
  }

  // Calculate radius from bounds (approximate)
  static calculateRadiusFromBounds(bounds) {
    // Use the Haversine formula to calculate distance between corners
    const R = 6371000; // Earth's radius in meters
    const lat1 = bounds.north * Math.PI / 180;
    const lat2 = bounds.south * Math.PI / 180;
    const deltaLat = (bounds.south - bounds.north) * Math.PI / 180;
    const deltaLng = (bounds.east - bounds.west) * Math.PI / 180;

    const a = Math.sin(deltaLat/2) * Math.sin(deltaLat/2) +
              Math.cos(lat1) * Math.cos(lat2) *
              Math.sin(deltaLng/2) * Math.sin(deltaLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c;

    // Return half the diagonal distance as radius
    return Math.round(distance / 2);
  }

  // Get search button text based on zoom level
  static getSearchButtonText(zoom) {
    if (zoom <= 11) return i18n.t('labels:searchButton.city');
    if (zoom <= 13) return i18n.t('labels:searchButton.district');
    return i18n.t('labels:searchButton.area');
  }

  // Get area description for user feedback
  /**
   * Search for Viator experiences matching a location name.
   * Queries cached viator_products — no external API call.
   */
  static async searchViatorExperiences(location) {
    if (!location) return [];
    try {
      const response = await fetch(
        `${PROXY_BASE_URL}/viator-experiences?location=${encodeURIComponent(location)}&limit=20`
      );
      if (!response.ok) return [];
      const data = await response.json();
      return data.experiences || [];
    } catch (error) {
      console.error('Error fetching Viator experiences:', error);
      return [];
    }
  }

  /**
   * Search Tiqets experiences by location (city + country).
   * Queries cached tiqets_experiences/products/tags — no external API call.
   * Backend derives Option-B schedule hints (is_recurring_show, schedule_chips)
   * from product sub-category + time-of-day/seasonal tags.
   */
  static async searchTiqetsExperiences(location) {
    if (!location) return [];
    try {
      const response = await fetch(
        `${PROXY_BASE_URL}/tiqets-experiences?location=${encodeURIComponent(location)}&limit=20`
      );
      if (!response.ok) return [];
      const data = await response.json();
      return data.experiences || [];
    } catch (error) {
      console.error('Error fetching Tiqets experiences:', error);
      return [];
    }
  }

  static getSearchAreaDescription(searchParams, locationName = '') {
    const radius = searchParams.radius / 1000; // Convert to km
    
    if (searchParams.searchStrategy === 'wide_area') {
      return `Searching ${locationName || 'this city'} (${radius.toFixed(0)}km radius)`;
    } else if (searchParams.searchStrategy === 'district') {
      return `Searching ${radius.toFixed(1)}km radius in this district`;
    } else {
      return `Searching ${radius.toFixed(1)}km around this area`;
    }
  }
}

export default GooglePlacesService;